import React, { useState, useEffect, useRef } from 'react';
import './career.css';
import MobileNav from '../common/MobileNav';

const Career = () => {
  const [activeModal, setActiveModal] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    resumeLink: ''  
  });

  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: ''
  });
  const heroRef = useRef(null);

  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Parallax effect
          if (heroRef.current) {
            const scrolled = window.pageYOffset;
            heroRef.current.style.transform = `translateY(${scrolled * 0.4}px)`;
          }

          // Scroll progress
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const scrollTop = window.pageYOffset;
          const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
          setScrollProgress(progress);

          // Back to top button
          setShowBackToTop(scrollTop > 300);

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = document.querySelectorAll('.stat-card, .job-card');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const stats = [
    {
      value: '3',
      label: 'Success in getting happy customer',
      icon: '😊'
    },
    {
      value: '5',
      label: 'Successful business',
      icon: '🚀'
    },
    {
      value: '6',
      label: 'Total clients who love Elexyt',
      icon: '❤️'
    },
    {
      value: '4',
      label: 'Stars reviews given by satisfied clients',
      icon: '⭐'
    }
  ];

  const jobs = [
    {
      id: 'webDesigner',
      category: 'Web Design',
      title: 'Web Designer',
      location: 'PAN India',
      image: 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=800',
      tag: 'Design',
      details: {
        company: 'Elexyt Technologies Pvt. Ltd.',
        description: 'We are seeking a talented Web Designer to join our team. The ideal candidate will have a keen eye for design, a passion for creating beautiful, user-friendly websites, and the ability to work closely with developers and clients to bring visions to life.',
        responsibilities: [
          'Design visually appealing and intuitive websites for various clients.',
          'Collaborate with developers to ensure design feasibility and functionality.',
          'Stay updated on the latest design trends and best practices.',
          'Create wireframes, prototypes, and mockups for client presentations.',
          'Maintain and update existing websites as needed.'
        ],
        requirements: [
          'Proven experience as a Web Designer or in a similar role.',
          'Proficiency in design tools like Adobe XD, Figma, or Sketch.',
          'Strong understanding of UX/UI principles.',
          'Experience with HTML, CSS, and JavaScript is a plus.',
          'Excellent communication and teamwork skills.',
          'A strong portfolio showcasing your design work.'
        ]
      }
    },
    {
      id: 'javaDeveloper',
      category: 'Java Development',
      title: 'Java Developer',
      location: 'PAN India',
      image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800',
      tag: 'Backend',
      details: {
        company: 'Elexyt Technologies Pvt. Ltd.',
        description: 'We are looking for a highly skilled Java Developer to join our growing team. The ideal candidate will have a strong understanding of Java programming and experience in building scalable applications. You will be working closely with our team of developers to create and maintain high-performance applications.',
        responsibilities: [
          'Design, develop, and maintain Java-based applications.',
          'Collaborate with cross-functional teams to define, design, and ship new features.',
          'Write clean, scalable code following best practices.',
          'Troubleshoot and debug applications to optimize performance.',
          'Participate in code reviews and contribute to a high-quality codebase.'
        ],
        requirements: [
          'Bachelor\'s degree in Computer Science or a related field.',
          'Proven experience as a Java Developer or similar role.',
          'Strong knowledge of Java, Spring Framework, and Hibernate.',
          'Experience with RESTful APIs and web services.',
          'Familiarity with front-end technologies like HTML, CSS, and JavaScript is a plus.',
          'Excellent problem-solving skills and attention to detail.'
        ]
      }
    },
    {
      id: 'mobileApp',
      category: 'Mobile App',
      title: 'Mobile App Developer',
      location: 'PAN India',
      image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800',
      tag: 'Mobile',
      details: {
        company: 'Elexyt Technologies Pvt. Ltd.',
        description: 'We are seeking a skilled Mobile App Developer to join our dynamic team. The ideal candidate will have a strong understanding of mobile application development and experience with both iOS and Android platforms. You will be responsible for building, maintaining, and enhancing mobile applications to meet our clients\' needs.',
        responsibilities: [
          'Develop and maintain mobile applications for iOS and Android platforms.',
          'Collaborate with cross-functional teams to define, design, and ship new features.',
          'Ensure the performance, quality, and responsiveness of applications.',
          'Identify and fix bugs and performance issues.',
          'Stay updated with the latest industry trends and technologies.'
        ],
        requirements: [
          'Proven experience as a Mobile App Developer.',
          'Strong proficiency in Swift, Kotlin, or Java.',
          'Experience with RESTful APIs and third-party libraries.',
          'Familiarity with UI/UX design principles.',
          'Knowledge of the full mobile development lifecycle.',
          'Excellent problem-solving skills and attention to detail.'
        ]
      }
    },
    {
      id: 'digitalMarketing',
      category: 'Digital Marketing',
      title: 'Digital Marketing Specialist',
      location: 'PAN India',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800',
      tag: 'Marketing',
      details: {
        company: 'Elexyt Technologies Pvt. Ltd.',
        description: 'We are looking for a talented Digital Marketing Specialist to join our growing team. The ideal candidate will have experience in creating, implementing, and optimizing digital marketing campaigns. You will work closely with our creative and content teams to drive online engagement and conversions.',
        responsibilities: [
          'Develop and execute digital marketing campaigns across various channels, including social media, email, and search engines.',
          'Analyze campaign performance and make data-driven decisions to optimize results.',
          'Collaborate with content creators to develop engaging marketing materials.',
          'Monitor and report on key metrics, such as ROI, conversion rates, and website traffic.',
          'Stay updated on the latest digital marketing trends and best practices.'
        ],
        requirements: [
          'Bachelor\'s degree in Marketing, Communications, or a related field.',
          'Proven experience in digital marketing or a related role.',
          'Strong knowledge of SEO, SEM, Google Analytics, and social media platforms.',
          'Experience with email marketing tools and CRM software.',
          'Excellent communication and analytical skills.',
          'Creative thinking and problem-solving abilities.'
        ]
      }
    },
    {
      id: 'graphicDesigner',
      category: 'Graphic Design',
      title: 'Graphic Designer',
      location: 'PAN India',
      image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800',
      tag: 'Creative',
      details: {
        company: 'Elexyt Technologies Pvt. Ltd.',
        description: 'We are looking for a talented Graphic Designer to join our creative team. The ideal candidate will have a keen eye for design, a passion for creativity, and experience in both digital and print media. You will work closely with our marketing and content teams to create visually appealing designs that align with our brand\'s identity.',
        responsibilities: [
          'Design digital and print materials including social media graphics, banners, brochures, and advertisements.',
          'Collaborate with the marketing team to develop creative concepts and campaigns.',
          'Ensure consistency in brand messaging and visual identity across all platforms.',
          'Manage multiple design projects simultaneously and meet deadlines.',
          'Stay updated with the latest design trends and tools.'
        ],
        requirements: [
          'Bachelor\'s degree in Graphic Design, Fine Arts, or a related field.',
          'Proven experience as a Graphic Designer or in a similar role.',
          'Proficiency in design software such as Adobe Creative Suite (Photoshop, Illustrator, InDesign).',
          'Strong portfolio showcasing a variety of design work.',
          'Excellent communication and teamwork skills.',
          'Ability to work in a fast-paced environment and manage multiple projects.'
        ]
      }
    },
    {
      id: 'frontendDeveloper',
      category: 'Frontend Developer',
      title: 'React Developer',
      location: 'PAN India',
      image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800',
      tag: 'Frontend',
      details: {
        company: 'Elexyt Technologies Pvt. Ltd.',
        description: 'We are looking for a skilled Front-End Developer to join our team. The ideal candidate will have experience with HTML, CSS, and JavaScript, and be familiar with modern frameworks such as React or Vue.js.',
        responsibilities: [
          'Develop and maintain web applications with a focus on user experience.',
          'Collaborate with designers to create intuitive and responsive designs.',
          'Optimize applications for maximum speed and scalability.',
          'Ensure cross-browser compatibility and performance.'
        ],
        requirements: [
          'Proven experience as a Front-End Developer.',
          'Strong proficiency in HTML, CSS, and JavaScript.',
          'Experience with React, Vue.js, or similar frameworks.',
          'Understanding of responsive design principles.',
          'Excellent problem-solving skills and attention to detail.'
        ]
      }
    }
  ];

  const openModal = (jobId) => {
    setActiveModal(jobId);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setActiveModal(null);
    document.body.style.overflow = 'unset';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  // Validate position
  if (!formData.position) {
    setToast({
      show: true,
      message: 'Please select a position you are applying for.',
      type: 'error'
    });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 5000);
    return;
  }

  // Validate resume link
  if (!formData.resumeLink) {
    setToast({
      show: true,
      message: 'Please provide your resume link (Google Drive / PDF link).',
      type: 'error'
    });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 5000);
    return;
  }

  // Simple URL validation
  try {
    new URL(formData.resumeLink);
  } catch {
    setToast({
      show: true,
      message: 'Please enter a valid resume URL.',
      type: 'error'
    });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 5000);
    return;
  }

  // Button loading state
  const submitButton = e.target.querySelector('button[type="submit"]');
  const originalText = submitButton.innerHTML;

  submitButton.innerHTML = `
    <span style="display:flex;align-items:center;gap:.5rem;">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" class="spinner-icon">
        <circle cx="12" cy="12" r="10" opacity="0.25"/>
        <path d="M12 2a10 10 0 0 1 10 10" opacity="0.75"/>
      </svg>
      Submitting...
    </span>
  `;
  submitButton.disabled = true;

  try {
    const payload = {
      access_key: 'ffe9b71b-4e54-41ed-bb2a-c52a8b9d9ce4',
      subject: `New Job Application - ${formData.position}`,
      from_name: 'Elexyt Careers',
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      replyto: formData.email,
      cc: "hr@elexyt.com",
      
      // Message to HR
      message: `
📌 NEW JOB APPLICATION RECEIVED
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

👤 Applicant Details
-------------------
Name  : ${formData.name}
Email : ${formData.email}
Phone : ${formData.phone}

💼 Position Applied For
----------------------
${formData.position}

📎 Resume Link
--------------
${formData.resumeLink}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Sent from Elexyt Career Portal
      `.trim()
    };

    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.message || 'Submission failed');
    }

    // Success
    setToast({
      show: true,
      message: 'Application submitted successfully! We will get back to you soon.',
      type: 'success'
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      position: '',
      resumeLink: ''
    });

  } catch (error) {
    
    
    let errorMessage = 'Failed to submit application. ';
    
    if (!navigator.onLine) {
      errorMessage += 'Please check your internet connection.';
    } else if (error.message) {
      errorMessage += error.message;
    } else {
      errorMessage += 'Please try again later.';
    }
    
    setToast({
      show: true,
      message: errorMessage,
      type: 'error'
    });
  } finally {
    submitButton.innerHTML = originalText;
    submitButton.disabled = false;

    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 5000);
  }
};

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      {/* Scroll Progress Bar */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />
      <div className="career-page">
        {/* Hero Section */}
        <section className="career-hero">
          <div className="career-hero-background" ref={heroRef}>
            <div className="career-hero-pattern"></div>
            <div className="career-hero-gradient"></div>
          </div>

          <div className="container">
            <div className="career-hero-content">
              <div className="hero-badge-wrapper">
                <span className="hero-badge">
                  <span className="badge-dot"></span>
                  Join Our Team
                </span>
              </div>

              <h1 className="career-hero-title">
                Build Your Career
                <span className="hero-title-gradient"> With Elexyt</span>
              </h1>

              <p className="career-hero-description">
                Join a dynamic team of innovators and creators. We're looking for talented individuals who are passionate about technology and eager to make an impact.
              </p>
            </div>
          </div>

          <div className="career-stats">
            <div className="container">
              <div className="stats-wrapper">
                {stats.map((stat, index) => (
                  <div key={index} className="stat-card">
                    <div className="stat-icon">{stat.icon}</div>
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Jobs Section */}
        <section className="career-jobs-section">
          <div className="container">
            <div className="section-header centered">
              <div className="section-label">
                <span className="label-line"></span>
                <span>Open Positions</span>
                <span className="label-line"></span>
              </div>
              <h2 className="section-title-large">
                Latest Jobs & Opportunities
              </h2>
              <p className="section-subtitle">
                Explore exciting career opportunities and join our talented team
              </p>
            </div>

            <div className="jobs-grid">
              {jobs.map((job, index) => (
                <div key={index} className="job-card">
                  <div className="job-image-wrapper">
                    <img src={job.image} alt={job.title} />
                    <div className="job-overlay">
                      <span className="job-tag">{job.tag}</span>
                    </div>
                  </div>
                  <div className="job-content">
                    <div className="job-header">
                      <span className="job-category">{job.category}</span>
                      {/* ✅ REMOVED: Calendar icon and last date */}
                    </div>
                    <h3 className="job-title">{job.title}</h3>
                    <p className="job-location">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      {job.location}
                    </p>
                    <div className="job-actions">
                      <button onClick={() => openModal(job.id)} className="job-link">
                        <span>View Details</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </button>
                      <a href="#applyForm" className="job-apply-btn">
                        Apply Now
                      </a>
                    </div>
                  </div>
                  <div className="job-card-glow"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Form Section */}
        <section id="applyForm" className="career-form-section">
          <div className="container">
            <div className="section-header centered">
              <div className="section-label">
                <span className="label-line"></span>
                <span>Apply Now</span>
                <span className="label-line"></span>
              </div>
              <h2 className="section-title-large">
                Submit Your Application
              </h2>
              <p className="section-subtitle">
                Take the first step towards an exciting career with Elexyt Technologies
              </p>
            </div>

            <div className="form-wrapper">
              <form onSubmit={handleSubmit} className="application-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="position">Position Applying For *</label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a position</option>
                    {jobs.map((job) => (
                      <option key={job.id} value={job.title}>
                        {job.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="resumeLink">Resume Link *</label>
                  <div className="file-input-wrapper">
                    <input
                      type="url"
                      id="resumeLink"
                      name="resumeLink"
                      placeholder="Paste Google Drive / PDF resume link"
                      value={formData.resumeLink}
                      onChange={(e) =>
                        setFormData({ ...formData, resumeLink: e.target.value })
                      }
                      required
                    />
                  </div>
                  <small className="form-hint">
                    Provide a publicly accessible resume link (PDF, DOC, or DOCX)
                  </small>
                </div>

                <button type="submit" className="btn btn-primary btn-large form-submit">
                  Submit Application
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Modals */}
        {jobs.map((job) =>
          activeModal === job.id ? (
            <div key={job.id} className="modal-overlay" onClick={closeModal}>
              <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={closeModal}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>

                <div className="modal-header">
                  <div className="modal-icon-wrapper">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                  </div>
                  <h2>{job.title}</h2>
                  <p className="modal-company">{job.details.company}</p>
                  <div className="modal-meta">
                    <span className="modal-location">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      {job.location}
                    </span>
                    <span className="modal-category">{job.category}</span>
                  </div>
                </div>

                <div className="modal-body">
                  <div className="modal-section">
                    <h3>Job Description</h3>
                    <p>{job.details.description}</p>
                  </div>

                  <div className="modal-section">
                    <h3>Key Responsibilities</h3>
                    <ul className="modal-list">
                      {job.details.responsibilities.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="modal-section">
                    <h3>Requirements</h3>
                    <ul className="modal-list">
                      {job.details.requirements.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="modal-cta">
                    <a href="#applyForm" onClick={closeModal} className="btn btn-primary btn-large">
                      Apply for this Position
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : null
        )}
        
        {/* Toast Notification */}
        {toast.show && (
          <div className={`toast-notification ${toast.type}`}>
            <div className="toast-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="toast-content">
              <p className="toast-message">{toast.message}</p>
            </div>
            <button className="toast-close" onClick={() => setToast({ show: false, message: '', type: '' })}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Back to Top Button */}
      <button
        className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
      <MobileNav />
    </>
  );
};

export default Career;